/**
 * Organization-domain derivation for the Meshtek Platform Directory
 * (meshtek-core; auth#5). The directory is keyed by DOMAIN — an entry
 * pre-associates a customer across family products, so the domain a
 * product registers must be one that product has EVIDENCE the customer
 * controls. Detect's evidence is the signup user's identity-provider
 * -verified email domain; nothing here is ever free text from a form.
 *
 * Two guards, both fail-closed (no domain → no registration, never a
 * guess):
 *  1. `normalizeOrgDomain` — the same canonicalisation meshtek-core
 *     applies (lowercase, trailing-dot stripped, real DNS labels only),
 *     so a domain that would be rejected server-side never leaves here.
 *  2. `isPublicEmailDomain` — a consumer-mailbox domain is NOT an
 *     organization. Claiming `gmail.com` would associate every Gmail
 *     signup with one org; those signups simply carry no domain.
 */
/** One DNS label: 1–63 chars, hyphens only in the interior. */
const LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
/**
 * Canonicalise a hostname for directory use, or return null when the
 * input isn't a bare, valid hostname. Mirrors meshtek-core's
 * `normalizeDomain` — but returns null instead of throwing, because on
 * this side an unusable domain is a normal outcome (skip registration),
 * not an error.
 */
export function normalizeOrgDomain(raw) {
    const d = raw.trim().toLowerCase().replace(/\.+$/, '');
    const labels = d.split('.');
    if (d.length === 0 ||
        d.length > 253 ||
        d.includes('/') ||
        d.includes(':') ||
        d.includes(' ') ||
        d.includes('@') ||
        labels.length < 2 ||
        !labels.every((label) => LABEL.test(label))) {
        return null;
    }
    return d;
}
/**
 * Consumer mailbox providers — a mailbox here proves an individual, not
 * an organization. Curated (not exhaustive): a provider we miss becomes
 * a junk claim on a domain nobody can release, so entries are added
 * readily and never removed. Country variants are listed explicitly
 * because suffix matching would swallow real company domains.
 */
const PUBLIC_EMAIL_DOMAINS = new Set([
    // Google
    'gmail.com', 'googlemail.com',
    // Microsoft
    'outlook.com', 'outlook.co.uk', 'outlook.fr', 'outlook.de', 'outlook.es',
    'outlook.it', 'outlook.com.au', 'hotmail.com', 'hotmail.co.uk', 'hotmail.fr',
    'hotmail.de', 'hotmail.it', 'hotmail.es', 'hotmail.ca', 'live.com',
    'live.co.uk', 'live.ca', 'live.com.au', 'msn.com', 'passport.com',
    // Yahoo / AOL
    'yahoo.com', 'yahoo.co.uk', 'yahoo.co.jp', 'yahoo.fr', 'yahoo.de',
    'yahoo.ca', 'yahoo.com.au', 'yahoo.co.in', 'yahoo.es', 'yahoo.it',
    'ymail.com', 'rocketmail.com', 'aol.com', 'aim.com',
    // Apple
    'icloud.com', 'me.com', 'mac.com',
    // Privacy-focused
    'proton.me', 'protonmail.com', 'pm.me', 'tutanota.com', 'tuta.io',
    'hushmail.com', 'fastmail.com', 'fastmail.fm',
    // Other global webmail
    'gmx.com', 'gmx.net', 'gmx.de', 'gmx.at', 'gmx.ch', 'mail.com',
    'email.com', 'zoho.com', 'zohomail.com', 'yandex.com', 'yandex.ru',
    'mail.ru', 'inbox.ru', 'list.ru', 'bk.ru', 'qq.com', '163.com', '126.com',
    'sina.com', 'naver.com', 'daum.net', 'hanmail.net', 'rediffmail.com',
    'web.de', 't-online.de', 'freenet.de', 'orange.fr', 'wanadoo.fr',
    'free.fr', 'laposte.net', 'sfr.fr', 'libero.it', 'virgilio.it',
    'seznam.cz', 'wp.pl', 'o2.pl', 'interia.pl', 'abv.bg', 'terra.com.br',
    'uol.com.br', 'bol.com.br',
    // ISP mailboxes (US/UK/CA)
    'comcast.net', 'verizon.net', 'att.net', 'sbcglobal.net', 'bellsouth.net',
    'cox.net', 'charter.net', 'earthlink.net', 'juno.com', 'netzero.net',
    'optonline.net', 'roadrunner.com', 'rr.com', 'twc.com', 'windstream.net',
    'frontier.com', 'centurylink.net', 'embarqmail.com', 'mindspring.com',
    'ameritech.net', 'pacbell.net', 'prodigy.net', 'btinternet.com',
    'sky.com', 'virginmedia.com', 'ntlworld.com', 'blueyonder.co.uk',
    'talktalk.net', 'rogers.com', 'shaw.ca', 'telus.net', 'sympatico.ca',
    'bigpond.com', 'bigpond.net.au', 'optusnet.com.au',
    // Disposable
    'mailinator.com', 'guerrillamail.com', 'yopmail.com', '10minutemail.com',
    'trashmail.com', 'sharklasers.com', 'temp-mail.org', 'dispostable.com',
]);
/** True when a domain is a consumer mailbox provider, not an organization. */
export function isPublicEmailDomain(domain) {
    const normalized = normalizeOrgDomain(domain);
    return normalized !== null && PUBLIC_EMAIL_DOMAINS.has(normalized);
}
/**
 * The organization domain implied by an email address, or null when
 * there isn't one we may act on (malformed address, non-hostname part,
 * or a consumer mailbox provider). Null is a normal answer: that
 * customer takes part in the directory only once an operator records a
 * domain for them out of band.
 */
export function orgDomainFromEmail(email) {
    const at = email.lastIndexOf('@');
    if (at < 1 || at === email.length - 1) {
        return null;
    }
    const domain = normalizeOrgDomain(email.slice(at + 1));
    if (domain === null || PUBLIC_EMAIL_DOMAINS.has(domain)) {
        return null;
    }
    return domain;
}
//# sourceMappingURL=org-domain.js.map